package com.dotdrives.web.pages.forms

import com.dotdrives.controller.NewFormController
import com.dotdrives.controller.NewFormUiState
import com.dotdrives.design.DotButtonVariant
import com.dotdrives.model.GivingForm
import com.dotdrives.model.GivingFormType
import com.dotdrives.model.Target
import com.dotdrives.web.ui.DotBanner
import com.dotdrives.web.ui.DotButton
import react.FC
import react.Key
import react.Props
import react.dom.aria.AriaRole
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.option
import react.dom.html.ReactHTML.select
import react.dom.html.ReactHTML.span
import web.cssom.ClassName
import web.html.ButtonType
import web.html.button as buttonType

external interface NewFormDialogProps : Props {
    var controller: NewFormController
    var state: NewFormUiState
    var targets: List<Target>
    var existingForms: List<GivingForm>
    var onCancel: () -> Unit
}

private val formTypes = listOf(GivingFormType.CAMPAIGN, GivingFormType.EVERGREEN, GivingFormType.EVENT)

val NewFormDialog = FC<NewFormDialogProps> { props ->
    val state = props.state
    val controller = props.controller

    div {
        className = ClassName("forms-dialog-backdrop")
        role = "presentation".unsafeCast<AriaRole>()
        onClick = { props.onCancel() }

        div {
            className = ClassName("forms-dialog")
            role = "dialog".unsafeCast<AriaRole>()
            ariaModal = true
            ariaLabel = "New giving form"
            onClick = { it.stopPropagation() }

            div { className = ClassName("forms-dialog-title"); +"New giving form" }

            state.error?.let { error -> DotBanner { message = error; className = "forms-inline-banner" } }

            div {
                className = ClassName("forms-type-list")
                formTypes.forEach { formType ->
                    val selected = state.type == formType
                    button {
                        key = formType.unsafeCast<Key>()
                        type = ButtonType.buttonType
                        className = ClassName(
                            "forms-type-option" + if (selected) " forms-type-option-selected" else "",
                        )
                        onClick = { controller.setType(formType) }
                        span {
                            className = ClassName("forms-type-emoji")
                            ariaHidden = true
                            +formTypeEmoji(formType)
                        }
                        div {
                            className = ClassName("forms-type-copy")
                            div { className = ClassName("forms-type-name"); +formTypeLabel(formType) }
                            div { className = ClassName("forms-type-desc"); +formTypeDescription(formType) }
                        }
                        if (selected) {
                            span { className = ClassName("forms-type-check"); ariaHidden = true; +"✓" }
                        }
                    }
                }
            }

            div {
                className = ClassName("forms-dialog-grid")
                div {
                    className = ClassName("forms-field-block")
                    div { className = ClassName("forms-field-label"); +"Donation target" }
                    div {
                        className = ClassName("forms-select-wrap")
                        select {
                            className = ClassName("forms-select")
                            ariaLabel = "Donation target"
                            value = state.targetId ?: ""
                            onChange = { controller.setTargetId(it.currentTarget.value.takeIf(String::isNotBlank)) }
                            option { value = ""; +"No target" }
                            props.targets.forEach { target ->
                                option { key = target.id.unsafeCast<Key>(); value = target.id; +target.name }
                            }
                        }
                        span { className = ClassName("forms-select-chevron"); ariaHidden = true; +"▾" }
                    }
                }
                div {
                    className = ClassName("forms-field-block")
                    div { className = ClassName("forms-field-label"); +"Start from" }
                    div {
                        className = ClassName("forms-select-wrap")
                        select {
                            className = ClassName("forms-select")
                            ariaLabel = "Start from an existing form"
                            value = state.copyFromFormId ?: ""
                            onChange = {
                                controller.setCopyFromFormId(it.currentTarget.value.takeIf(String::isNotBlank))
                            }
                            option { value = ""; +"Blank form" }
                            props.existingForms.forEach { existing ->
                                option {
                                    key = existing.id.unsafeCast<Key>()
                                    value = existing.id
                                    +"${existing.name} (copy)"
                                }
                            }
                        }
                        span { className = ClassName("forms-select-chevron"); ariaHidden = true; +"▾" }
                    }
                }
            }

            div {
                className = ClassName("forms-dialog-actions")
                DotButton {
                    text = "Cancel"
                    variant = DotButtonVariant.Secondary
                    onClick = props.onCancel
                    enabled = !state.saving
                }
                DotButton {
                    text = "Create & open builder →"
                    onClick = { controller.create(props.targets, props.existingForms) }
                    loading = state.saving
                }
            }
        }
    }
}
